/**
 * Program Governance Service
 * Governance reviews, decisions, change requests, and compliance
 */
import { apiClient } from './api'

export interface GovernanceReview {
  id: number
  program_id: number
  review_type: string
  reviewer_id?: number
  reviewer_role?: string
  outcome: string
  notes?: string
  review_date: string
  created_at: string
}

export interface Decision {
  id: number
  program_id: number
  decision_text: string
  decision_maker_id?: number
  decision_date: string
  impact?: string
  rationale?: string
  metadata?: Record<string, unknown>
}

export interface ChangeRequest {
  id: number
  program_id: number
  request_type: string
  title: string
  description?: string
  requested_by_id?: number
  status: string
  impact_analysis?: string
  approved_by_id?: number
  approved_at?: string
  created_at: string
  metadata?: Record<string, unknown>
}

export interface Compliance {
  id: number
  program_id: number
  compliance_score: number
  compliance_type: string
  last_assessed: string
  next_assessment?: string
  findings?: Record<string, unknown>
  metadata?: Record<string, unknown>
}

export const programGovernanceService = {
  /**
   * List governance reviews
   */
  async listReviews(
    programId: number,
    reviewType?: string,
    outcome?: string
  ): Promise<GovernanceReview[]> {
    const params: Record<string, unknown> = {}
    if (reviewType) params.review_type = reviewType
    if (outcome) params.outcome = outcome
    
    const response = await apiClient.get(
      `/api/v1/programs/${programId}/governance/reviews`,
      { params }
    )
    return response.data
  },

  /**
   * Create governance review
   */
  async createReview(programId: number, data: Partial<GovernanceReview>): Promise<GovernanceReview> {
    const response = await apiClient.post(
      `/api/v1/programs/${programId}/governance/reviews`,
      data
    )
    return response.data
  },

  /**
   * List decisions
   */
  async listDecisions(
    programId: number,
    impact?: string
  ): Promise<Decision[]> {
    const params: Record<string, unknown> = {}
    if (impact) params.impact = impact
    
    const response = await apiClient.get(
      `/api/v1/programs/${programId}/governance/decisions`,
      { params }
    )
    return response.data
  },

  /**
   * Create decision
   */
  async createDecision(programId: number, data: Partial<Decision>): Promise<Decision> {
    const response = await apiClient.post(
      `/api/v1/programs/${programId}/governance/decisions`,
      data
    )
    return response.data
  },

  /**
   * List change requests
   */
  async listChangeRequests(
    programId: number,
    requestType?: string,
    status?: string
  ): Promise<ChangeRequest[]> {
    const params: Record<string, unknown> = {}
    if (requestType) params.request_type = requestType
    if (status) params.status = status
    
    const response = await apiClient.get(
      `/api/v1/programs/${programId}/governance/change-requests`,
      { params }
    )
    return response.data
  },

  /**
   * Create change request
   */
  async createChangeRequest(programId: number, data: Partial<ChangeRequest>): Promise<ChangeRequest> {
    const response = await apiClient.post(
      `/api/v1/programs/${programId}/governance/change-requests`,
      data
    )
    return response.data
  },

  /**
   * Approve or reject change request
   */
  async approveChangeRequest(
    programId: number,
    requestId: number,
    approved: boolean = true
  ): Promise<ChangeRequest> {
    const response = await apiClient.put(
      `/api/v1/programs/${programId}/governance/change-requests/${requestId}/approve`,
      null,
      { params: { approved } }
    )
    return response.data
  },

  /**
   * Get compliance score
   */
  async getCompliance(
    programId: number,
    complianceType?: string
  ): Promise<Compliance> {
    const params: Record<string, unknown> = {}
    if (complianceType) params.compliance_type = complianceType
    
    const response = await apiClient.get(
      `/api/v1/programs/${programId}/governance/compliance`,
      { params }
    )
    return response.data
  },

  /**
   * Create or update compliance
   */
  async createCompliance(programId: number, data: Partial<Compliance>): Promise<Compliance> {
    const response = await apiClient.post(
      `/api/v1/programs/${programId}/governance/compliance`,
      data
    )
    return response.data
  },
}

